/**
 * Finestra a piena pagina.
 *
 * Serve quando il contenuto ha bisogno di *tutto* lo spazio — il calendario di
 * un immobile, per esempio: due mesi affiancati non stanno in una colonna. Si
 * chiude con la X, con Esc o cliccando fuori: tre uscite, perché una finestra
 * che copre lo schermo deve essere facilissima da togliere.
 *
 * Tre cose che prima non faceva, e che si notano solo quando mancano:
 *
 *  - **Esc chiude una finestra sola.** Ogni finestra aperta registrava il suo
 *    ascoltatore, e un Esc le chiudeva tutte insieme: chi apriva il calendario
 *    dalla scheda di un immobile si ritrovava fuori da tutte e due. Adesso
 *    c'è una pila, e il tasto parla all'ultima arrivata;
 *  - **il fuoco resta dentro.** Con Tab si usciva dalla finestra e si finiva
 *    sui bottoni della pagina sotto, che non si vedono nemmeno;
 *  - **la pagina sotto non scorre**, come già faceva `Sheet`;
 *  - **copre tutta la pagina, da qualunque punto la si apra.** Si monta
 *    direttamente nel `body`: un antenato con un'animazione (`transform`)
 *    diventa il riferimento dei `fixed`, e il fascicolo aperto dalle
 *    impostazioni restava chiuso nella colonna della sezione, con l'ombra a
 *    metà pagina.
 */

import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, X } from "lucide-react";
import { cx } from "./primitives.tsx";

/**
 * La pila delle finestre aperte.
 *
 * Vive fuori da React di proposito: è un fatto del documento — «quale finestra
 * sta sopra» — e ogni istanza deve vedere lo stesso, senza contesto da
 * infilare in mezzo a chi usa il componente.
 */
const pila: symbol[] = [];

/**
 * Com'era lo scorrimento della pagina prima che si aprisse la prima finestra.
 *
 * Vive qui e non dentro l'effetto perché con due finestre aperte la seconda
 * lo leggerebbe già a `hidden` — messo dalla prima — e chiudendole entrambe
 * lo rimetterebbe a `hidden` per sempre: la pagina sotto non scorrerebbe mai
 * più, e sembrerebbe bloccata senza nessuna finestra in vista.
 */
let scorrimentoOriginale = "";

/** Gli elementi su cui si può andare con Tab. */
const FUOCABILI = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export function Modal({
  open,
  onClose,
  onBack,
  backLabel = "Indietro",
  title,
  subtitle,
  media,
  footer,
  bleed,
  stretta,
  sopraLaBarra,
  children,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * La strada per tornare da dove si è venuti, oltre alla X. Una finestra
   * aperta da una scheda deve dire «torno alla scheda», non solo «chiudo».
   */
  onBack?: () => void;
  backLabel?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  media?: ReactNode;
  footer?: ReactNode;
  /** Contenuto a filo: le schermate a due colonne non vogliono margini. */
  bleed?: boolean;
  /**
   * Una finestra piccola, al centro, alta quanto il contenuto: per una
   * domanda sola (una scheda del quaderno), dove il tutto schermo sarebbe
   * un muro bianco con tre righe in mezzo.
   */
  stretta?: boolean;
  /**
   * Sul telefono la finestra si ferma sopra la barra in basso, che resta
   * visibile e si può toccare: Oggi, Messaggi e Immobili sono schermate, e
   * da una schermata si deve poter passare a un'altra senza chiuderla
   * prima. Su schermo largo non cambia niente.
   */
  sopraLaBarra?: boolean;
  children: ReactNode;
}) {
  const riquadro = useRef<HTMLDivElement | null>(null);
  // L'identità di *questa* finestra nella pila, stabile fra un render e l'altro.
  const io = useRef<symbol>(Symbol("modal"));

  /**
   * Chi chiude, in un riferimento.
   *
   * Quasi tutti passano una funzione scritta al volo, nuova a ogni render: con
   * `onClose` fra le dipendenze l'effetto ripartiva a ogni tasto premuto, e
   * ogni volta rimetteva il fuoco sul primo bottone della finestra — la X.
   * Scrivendo «elimina il mio account», il primo spazio premeva la X e la
   * finestra si chiudeva sotto le dita. L'effetto riparte solo quando la
   * finestra si apre o si chiude.
   */
  const chiudi = useRef(onClose);
  chiudi.current = onClose;

  useEffect(() => {
    if (!open) return;

    const mio = io.current;
    pila.push(mio);

    const onKey = (event: KeyboardEvent) => {
      // Solo l'ultima arrivata ascolta: sotto ce ne può essere un'altra, e
      // chiuderle tutte con un tasto solo è un modo di perdere il posto.
      if (pila[pila.length - 1] !== mio) return;

      if (event.key === "Escape") {
        event.stopPropagation();
        chiudi.current();
        return;
      }

      if (event.key !== "Tab") return;

      // Il fuoco resta dentro: senza, con Tab si finisce sui bottoni della
      // pagina sotto, che sono coperti e non si vedono.
      const dentro = riquadro.current?.querySelectorAll<HTMLElement>(FUOCABILI);
      if (!dentro || dentro.length === 0) return;

      const primo = dentro[0]!;
      const ultimo = dentro[dentro.length - 1]!;
      const attivo = document.activeElement;

      if (
        event.shiftKey &&
        (attivo === primo || !riquadro.current?.contains(attivo))
      ) {
        event.preventDefault();
        ultimo.focus();
      } else if (!event.shiftKey && attivo === ultimo) {
        event.preventDefault();
        primo.focus();
      }
    };

    document.addEventListener("keydown", onKey);

    // La pagina sotto non deve scorrere mentre si legge sopra: è quello che
    // fa sembrare «incollata» una finestra su telefono. Solo la prima della
    // pila si ricorda com'era.
    if (pila.length === 1) scorrimentoOriginale = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Il fuoco entra nella finestra appena si apre, così la tastiera lavora
    // dove sta guardando l'occhio.
    const daFuori = document.activeElement as HTMLElement | null;
    riquadro.current?.querySelector<HTMLElement>(FUOCABILI)?.focus();

    return () => {
      document.removeEventListener("keydown", onKey);
      const posizione = pila.indexOf(mio);
      if (posizione !== -1) pila.splice(posizione, 1);
      // Lo scorrimento torna solo quando non resta niente sopra.
      if (pila.length === 0)
        document.body.style.overflow = scorrimentoOriginale;
      daFuori?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      data-modale
      className={cx(
        "fixed inset-0 z-50 flex flex-col p-0 sm:p-6 lg:p-10",
        stretta && "justify-end sm:justify-center",
        sopraLaBarra &&
          "max-lg:bottom-[var(--altezza-barra)] max-lg:installata:bottom-[calc(var(--altezza-barra)+env(safe-area-inset-bottom))]",
      )}
    >
      <button
        type="button"
        aria-label="Chiudi"
        onClick={onClose}
        className="animate-fade absolute inset-0 bg-ink/55"
      />

      <div
        ref={riquadro}
        role="dialog"
        aria-modal="true"
        className={cx(
          "animate-modal relative flex min-h-0 w-full flex-col overflow-hidden bg-surface",
          "sm:mx-auto sm:rounded-card-lg sm:shadow-lift",
          stretta
            ? "max-h-full rounded-t-card-lg sm:max-w-[560px]"
            : "flex-1 sm:max-w-[1240px]",
        )}
      >
        <header className="flex items-center gap-3 border-b border-line px-5 py-4 sm:px-7">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              aria-label={backLabel}
              className="flex h-10 shrink-0 items-center gap-1.5 rounded-pill bg-surface-muted pl-3 pr-4 text-[13px] font-bold text-ink transition-all duration-quick hover:bg-surface-sunken active:scale-95"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">{backLabel}</span>
            </button>
          ) : null}
          {media}
          <div className="min-w-0 flex-1">
            <p className="truncate text-[18px] font-extrabold leading-tight text-ink">
              {title}
            </p>
            {subtitle ? (
              <p className="truncate text-[13px] text-ink-soft">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="grid size-10 shrink-0 place-items-center rounded-field text-ink-soft transition-all duration-quick hover:bg-surface-muted hover:text-ink active:scale-95"
          >
            <X size={19} />
          </button>
        </header>

        <div
          className={cx(
            "min-h-0 flex-1",
            bleed
              ? "overflow-hidden"
              : "scroll-soft overflow-y-auto px-5 py-6 sm:px-7",
          )}
        >
          {children}
        </div>

        {footer ? (
          // Fondo diverso dal corpo: la barra delle azioni deve staccarsi da
          // quello che ha sotto, altrimenti a fine schermo si confonde con la
          // prima cosa bianca che incontra.
          <footer className="border-t border-line bg-surface-muted px-5 py-4 sm:px-7">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
